import React from 'react';
import { Image as ImageIcon } from 'lucide-react';

interface ShotPlaceholderProps {
  /** What screenshot goes here. Shown on the placeholder. */
  label: string;
  /** Optional second line of guidance. */
  hint?: string;
  /** Aspect ratio helper class, e.g. 'aspect-[16/10]'. */
  ratio?: string;
  tone?: 'light' | 'dark' | 'yellow';
  className?: string;
}

/**
 * Clearly-labeled screenshot placeholder for marketing pages.
 * Swap each instance for a real <img> once screenshots are captured.
 */
const ShotPlaceholder: React.FC<ShotPlaceholderProps> = ({
  label,
  hint,
  ratio = 'aspect-[16/10]',
  tone = 'light',
  className = '',
}) => {
  const tones = {
    light: 'bg-gray-50 border-gray-300 text-gray-500',
    dark: 'bg-white/5 border-white/25 text-white/70',
    yellow: 'bg-white/10 border-gray-900/25 text-gray-800',
  } as const;

  return (
    <div
      className={`relative w-full ${ratio} rounded-2xl border-2 border-dashed overflow-hidden flex items-center justify-center ${tones[tone]} ${className}`}
    >
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, currentColor 0 1px, transparent 1px 12px)',
          opacity: 0.06,
        }}
      />
      <div className="relative text-center px-6 py-4">
        <ImageIcon className="w-7 h-7 mx-auto mb-2 opacity-70" />
        <div className="text-[10px] font-bold tracking-[0.18em] uppercase opacity-70 mb-1">
          Screenshot placeholder
        </div>
        <div className="text-sm font-semibold leading-snug">{label}</div>
        {hint && <div className="text-xs mt-1 opacity-75 leading-snug">{hint}</div>}
      </div>
    </div>
  );
};

export default ShotPlaceholder;
